import moment from 'moment';
import { useCallback, useMemo } from 'react';

import { useLoggedInUserRoles } from '~/components/Auth';
import { useCompanyData } from '~/data/company';
import { useAllEmployees } from '~/data/employees';
import { Roles } from '~/data/user';
import { hasOneOfRoles } from '~/services/Permissions/roles';

const ENDED_EMPLOYEE_VIEW_ROLES = [
  Roles.HR_ADMIN,
  Roles.HR_ADMIN_LIMITED,
  Roles.HR_STAFF,
];

type EndedEmployeeStateCheckValues = {
  endDate: Date | string | undefined;
  hasEndedStatus?: boolean;
};

type UseIsEndedEmployeeViewRestrictedOutput = {
  isAccessToEndedEmployeeRestrictedByEmploymentStatus: (
    props: EndedEmployeeStateCheckValues,
  ) => boolean;
  isAccessToEndedEmployeeRestricted: (employeeId?: number) => boolean;
};

export const useIsEndedEmployeeViewRestricted =
  (): UseIsEndedEmployeeViewRestrictedOutput => {
    const { companyData } = useCompanyData();
    const loggedInUserRoles = useLoggedInUserRoles();
    const { getEmployeeById } = useAllEmployees();

    const restrictionConfig = useMemo(() => {
      const editingLimit =
        companyData?.config?.fComFormerEmployeeDataEditingLimit || 0;

      const isUnrestrictedUser = hasOneOfRoles(
        loggedInUserRoles,
        ENDED_EMPLOYEE_VIEW_ROLES,
      );

      return {
        isRestrictionDisabled: editingLimit === -1,
        isUnrestrictedUser,
        editingLimit,
      };
    }, [companyData, loggedInUserRoles]);

    const isAccessToEndedEmployeeRestrictedByEmploymentStatus = useCallback(
      (props: EndedEmployeeStateCheckValues) => {
        const { endDate, hasEndedStatus } = props;

        const { isRestrictionDisabled, isUnrestrictedUser, editingLimit } =
          restrictionConfig;

        const hasNoEndDate = typeof endDate === 'undefined' || !endDate;
        if (
          isRestrictionDisabled ||
          isUnrestrictedUser ||
          (hasNoEndDate && !hasEndedStatus)
        ) {
          return false;
        }
        if (editingLimit > 0 && endDate) {
          return moment().diff(endDate, 'days') > editingLimit;
        }
        return true;
      },
      [restrictionConfig],
    );

    const isAccessToEndedEmployeeRestricted = useCallback(
      (employeeId?: number) => {
        const { isRestrictionDisabled, isUnrestrictedUser } = restrictionConfig;

        if (isRestrictionDisabled || isUnrestrictedUser) {
          return false;
        }

        const foundEmployee = getEmployeeById(employeeId);

        return isAccessToEndedEmployeeRestrictedByEmploymentStatus({
          endDate: foundEmployee?.fEmplEndDate,
          hasEndedStatus: !foundEmployee,
        });
      },
      [
        restrictionConfig,
        getEmployeeById,
        isAccessToEndedEmployeeRestrictedByEmploymentStatus,
      ],
    );

    return {
      isAccessToEndedEmployeeRestrictedByEmploymentStatus,
      isAccessToEndedEmployeeRestricted,
    };
  };
